import { addDays, format, parseISO, startOfWeek } from "date-fns";
import { weekStartsOn$ } from "../state/weekStartsOn";
import { isForecastSlotEditable } from "./forecastDateUtils";
import { buildFiscalYearWeekStartKeys, fiscalWeekKeyFromApiWeek, } from "./forecastTableWeekUtils";
import { generateTimeSlots } from "./forecastTimeSlotTransform";
const millionScaleTwoDecimalsTruncated = (scaled) => {
    const truncated = Math.floor(scaled * 100) / 100;
    return truncated.toFixed(2);
};
export const forecastNumberFormatter = {
    format(value) {
        const abs = Math.abs(value);
        const sign = value < 0 ? "-" : "";
        if (abs < 1000) {
            return `${sign}${abs.toFixed(2)}`;
        }
        if (abs < 1_000_000) {
            const scaled = abs / 1_000;
            return `${sign}${scaled.toFixed(2)}K`;
        }
        if (abs < 1_000_000_000) {
            const scaled = abs / 1_000_000;
            const fixed = abs >= 10_000_000
                ? millionScaleTwoDecimalsTruncated(scaled)
                : scaled.toFixed(2);
            return `${sign}${fixed}M`;
        }
        const scaled = abs / 1_000_000_000;
        const fixed = abs >= 10_000_000_000
            ? millionScaleTwoDecimalsTruncated(scaled)
            : scaled.toFixed(2);
        return `${sign}${fixed}B`;
    },
};
const getForecastValue = (forecast) => {
    return (forecast.aggregatedForecast ??
        forecast.draftForecast ??
        forecast.adjustedForecast ??
        forecast.originalForecast ??
        0);
};
export const transformDayForecast = (response) => {
    if (!response?.drivers || response.drivers.length === 0) {
        return { tableGraphForecastData: [], timeSlots: generateTimeSlots() };
    }
    const timeSlots = generateTimeSlots();
    const byKey = new Map();
    for (const driver of response.drivers) {
        const subType = driver.subType ?? "";
        const key = `${driver.name}::${subType}`;
        let acc = byKey.get(key);
        if (!acc) {
            const emptySlots = Object.fromEntries(timeSlots.map((slot) => [slot, 0]));
            acc = {
                driverLabel: driver.label,
                total: 0,
                timeSlots: emptySlots,
            };
            byKey.set(key, acc);
        }
        for (const interval of driver.forecast) {
            const slot = interval.time;
            if (slot in acc.timeSlots) {
                const value = getForecastValue(interval);
                acc.timeSlots[slot] += value;
                acc.total += value;
            }
        }
    }
    const tableGraphForecastData = Array.from(byKey.values()).map((acc) => ({
        driverLabel: acc.driverLabel,
        total: Math.round(acc.total),
        timeSlots: acc.timeSlots,
    }));
    return { tableGraphForecastData, timeSlots };
};
const buildWeekTimeSlotsOrdered = (weekStartDate, weekEndDate, weekStartsOn) => {
    const start = parseISO(weekStartDate);
    const end = weekEndDate != null && weekEndDate !== ""
        ? parseISO(weekEndDate)
        : addDays(start, 6);
    const dates = [];
    for (let d = start; d.getTime() <= end.getTime(); d = addDays(d, 1)) {
        dates.push(format(d, "yyyy-MM-dd"));
    }
    if (dates.length === 0)
        return [];
    const firstDayOfWeek = start.getDay();
    const rotation = (weekStartsOn - firstDayOfWeek + 7) % 7;
    if (rotation === 0)
        return dates;
    return [...dates.slice(rotation), ...dates.slice(0, rotation)];
};
export const transformWeekForecast = (response) => {
    if (!response) {
        return { tableGraphForecastData: [], timeSlots: [] };
    }
    const { weekStartDate, weekEndDate, drivers } = response;
    if (!weekStartDate) {
        return { tableGraphForecastData: [], timeSlots: [] };
    }
    const timeSlots = buildWeekTimeSlotsOrdered(weekStartDate, weekEndDate, weekStartsOn$.value);
    if (drivers.length === 0) {
        return { tableGraphForecastData: [], timeSlots };
    }
    const byKey = new Map();
    for (const driver of drivers) {
        const subType = driver.subType ?? "";
        const key = `${driver.name}::${subType}`;
        let acc = byKey.get(key);
        if (!acc) {
            const emptySlots = Object.fromEntries(timeSlots.map((slot) => [slot, 0]));
            acc = {
                driverLabel: driver.label,
                total: 0,
                timeSlots: emptySlots,
            };
            byKey.set(key, acc);
        }
        for (const day of driver.forecast) {
            const date = day.date;
            if (!date)
                continue;
            if (date in acc.timeSlots) {
                const value = getForecastValue(day);
                acc.timeSlots[date] += value;
                acc.total += value;
            }
        }
    }
    const tableGraphForecastData = Array.from(byKey.values()).map((acc) => ({
        driverLabel: acc.driverLabel,
        total: Math.round(acc.total),
        timeSlots: acc.timeSlots,
    }));
    return { tableGraphForecastData, timeSlots };
};
export const transformYearForecast = (response) => {
    if (!response?.drivers || response.drivers.length === 0) {
        return { tableGraphForecastData: [], timeSlots: [] };
    }
    const calendarStartYear = response.year;
    const timeSlots = buildFiscalYearWeekStartKeys(calendarStartYear, weekStartsOn$.value);
    const byKey = new Map();
    for (const driver of response.drivers) {
        const subType = driver.subType ?? "";
        const key = `${driver.name}::${subType}`;
        let acc = byKey.get(key);
        if (!acc) {
            const emptySlots = Object.fromEntries(timeSlots.map((slot) => [slot, 0]));
            acc = {
                driverLabel: driver.label,
                total: 0,
                timeSlots: emptySlots,
            };
            byKey.set(key, acc);
        }
        for (const week of driver.forecast) {
            const normalized = fiscalWeekKeyFromApiWeek(week.weekStartDate, week.weekEndDate, calendarStartYear, weekStartsOn$.value);
            if (normalized === "")
                continue;
            if (!(normalized in acc.timeSlots))
                continue;
            const value = getForecastValue(week);
            acc.timeSlots[normalized] += value;
            acc.total += value;
        }
    }
    const tableGraphForecastData = Array.from(byKey.values()).map((acc) => ({
        driverLabel: acc.driverLabel,
        total: Math.round(acc.total),
        timeSlots: acc.timeSlots,
    }));
    return { tableGraphForecastData, timeSlots };
};
const timeslotDisplay = (params) => {
    const original = params.originalForecast ?? 0;
    const adjusted = params.adjustedForecast ?? null;
    const draft = params.draftForecast ?? null;
    const aggregated = params.aggregatedForecast ?? null;
    const displayValue = aggregated ?? draft ?? adjusted ?? original;
    const percentage = original !== 0 ? displayValue / original : 0;
    const editable = isForecastSlotEditable(params.view, params.slotKey, params.referenceDate);
    return {
        id: params.id,
        originalForecast: original,
        adjustedForecast: adjusted,
        draftForecast: draft,
        aggregatedForecast: aggregated,
        displayValue,
        percentage,
        editable,
    };
};
const aggregateSubTypes = new Set(["ALL", "M2"]);
export function isAggregateSubType(subType) {
    return subType != null && aggregateSubTypes.has(subType);
}
function sortTableRowsWithAllFirst(rows) {
    return [...rows].sort((a, b) => {
        const aAgg = isAggregateSubType(a.subType);
        const bAgg = isAggregateSubType(b.subType);
        if (aAgg === bAgg)
            return 0;
        return aAgg ? -1 : 1;
    });
}
export const buildForecastTableRows = (raw, view, referenceDate) => {
    if (!raw?.drivers || raw.drivers.length === 0) {
        return { tableRows: [], timeSlots: [] };
    }
    const effectiveRefDate = referenceDate !== ""
        ? referenceDate
        : view === "day"
            ? raw.date ?? ""
            : view === "week"
                ? raw.weekStartDate ?? ""
                : format(startOfWeek(new Date()), "yyyy-MM-dd");
    if (view === "day") {
        const response = raw;
        const timeSlots = generateTimeSlots();
        const normalizeTime = (value) => value != null ? value.slice(0, 5) : "";
        const tableRows = response.drivers.map((driver) => {
            const byTime = new Map(driver.forecast.map((item) => [normalizeTime(item.time), item]));
            const timeSlotsRecord = {};
            let total = 0;
            for (const slot of timeSlots) {
                const item = byTime.get(slot);
                const original = item?.originalForecast ?? 0;
                const adjusted = item?.adjustedForecast ?? null;
                const draft = item?.draftForecast ?? null;
                const aggregated = item?.aggregatedForecast ?? null;
                const displayValue = aggregated ?? draft ?? adjusted ?? original;
                total += displayValue;
                timeSlotsRecord[slot] = timeslotDisplay({
                    id: item?.id ?? "",
                    originalForecast: original,
                    adjustedForecast: adjusted,
                    draftForecast: draft,
                    aggregatedForecast: aggregated,
                    slotKey: slot,
                    view: "day",
                    referenceDate: effectiveRefDate,
                });
            }
            return {
                driver: driver.label,
                subType: driver.subType ?? undefined,
                total: Math.round(total),
                timeSlots: timeSlotsRecord,
            };
        });
        return {
            tableRows: sortTableRowsWithAllFirst(tableRows),
            timeSlots,
        };
    }
    if (view === "week") {
        const response = raw;
        const timeSlots = buildWeekTimeSlotsOrdered(response.weekStartDate, response.weekEndDate, weekStartsOn$.value);
        const normalizeDate = (value) => value != null ? value.slice(0, 10) : "";
        const tableRows = response.drivers.map((driver) => {
            const byDate = new Map(driver.forecast.map((item) => [normalizeDate(item.date), item]));
            const timeSlotsRecord = {};
            let total = 0;
            for (const slot of timeSlots) {
                const item = byDate.get(slot);
                const original = item?.originalForecast ?? 0;
                const adjusted = item?.adjustedForecast ?? null;
                const draft = item?.draftForecast ?? null;
                const aggregated = item?.aggregatedForecast ?? null;
                const displayValue = aggregated ?? draft ?? adjusted ?? original;
                total += displayValue;
                timeSlotsRecord[slot] = timeslotDisplay({
                    id: item?.id ?? "",
                    originalForecast: original,
                    adjustedForecast: adjusted,
                    draftForecast: draft,
                    aggregatedForecast: aggregated,
                    slotKey: slot,
                    view: "week",
                    referenceDate: effectiveRefDate,
                });
            }
            return {
                driver: driver.label,
                subType: driver.subType ?? undefined,
                total: Math.round(total),
                timeSlots: timeSlotsRecord,
            };
        });
        return {
            tableRows: sortTableRowsWithAllFirst(tableRows),
            timeSlots,
        };
    }
    if (view === "year") {
        const response = raw;
        const calendarStartYear = response.year;
        const timeSlots = buildFiscalYearWeekStartKeys(calendarStartYear, weekStartsOn$.value);
        const tableRows = response.drivers.map((driver) => {
            const byWeek = new Map();
            for (const item of driver.forecast) {
                const normalized = fiscalWeekKeyFromApiWeek(item.weekStartDate, item.weekEndDate, calendarStartYear, weekStartsOn$.value);
                if (normalized !== "") {
                    byWeek.set(normalized, item);
                }
            }
            const timeSlotsRecord = {};
            let total = 0;
            for (const slot of timeSlots) {
                const item = byWeek.get(slot);
                const original = item?.originalForecast ?? 0;
                const adjusted = item?.adjustedForecast ?? null;
                const draft = item?.draftForecast ?? null;
                const aggregated = item?.aggregatedForecast ?? null;
                const displayValue = aggregated ?? draft ?? adjusted ?? original;
                total += displayValue;
                timeSlotsRecord[slot] = timeslotDisplay({
                    id: item?.id ?? "",
                    originalForecast: original,
                    adjustedForecast: adjusted,
                    draftForecast: draft,
                    aggregatedForecast: aggregated,
                    slotKey: slot,
                    view: "year",
                    referenceDate: effectiveRefDate,
                });
            }
            return {
                driver: driver.label,
                subType: driver.subType ?? undefined,
                total: Math.round(total),
                timeSlots: timeSlotsRecord,
            };
        });
        return {
            tableRows: sortTableRowsWithAllFirst(tableRows),
            timeSlots,
        };
    }
    return { tableRows: [], timeSlots: [] };
};
export const mapDriversToMenuItems = (drivers) => drivers.map((driver) => ({
    id: driver.id,
    title: driver.displayName,
}));
export const mapSubTypesToMenuItems = (subTypes) => subTypes.map((subType) => ({
    id: subType.type,
    title: subType.label,
}));
export const getDriverIdsByName = (drivers, driverName) => {
    if (driverName == null || driverName === "")
        return [];
    return drivers
        .filter((driver) => driver.name === driverName)
        .map((driver) => driver.id);
};
//# sourceMappingURL=forecastDataTransform.js.map